import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { jobsApi } from '@/lib/api';
import { 
  Truck, 
  Search,
  MapPin, 
  Calendar,
  Package,
  Weight,
  ArrowRight,
  Filter,
  RefreshCw,
  Loader2,
  X
} from 'lucide-react';

const states = [
  { code: '', name: 'All States' },
  { code: 'NSW', name: 'New South Wales' },
  { code: 'VIC', name: 'Victoria' },
  { code: 'QLD', name: 'Queensland' },
  { code: 'WA', name: 'Western Australia' },
  { code: 'SA', name: 'South Australia' },
  { code: 'TAS', name: 'Tasmania' },
  { code: 'ACT', name: 'Australian Capital Territory' },
  { code: 'NT', name: 'Northern Territory' }
];

const loadTypes = [
  { value: '', label: 'All Types' },
  { value: 'General Freight', label: 'General Freight' },
  { value: 'Equipment', label: 'Equipment' },
  { value: 'Machinery', label: 'Machinery' },
  { value: 'Auto Parts', label: 'Auto Parts' },
  { value: 'Construction Materials', label: 'Construction Materials' },
  { value: 'Oil Field Equipment', label: 'Oil Field Equipment' },
  { value: 'Agricultural', label: 'Agricultural' },
  { value: 'Livestock', label: 'Livestock' },
  { value: 'Other', label: 'Other' }
];

const sortOptions = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'weight_high', label: 'Weight: High to Low' },
  { value: 'weight_low', label: 'Weight: Low to High' }
];

export default function JobBrowsePage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  const [filters, setFilters] = useState({
    search: '',
    pickupState: '',
    deliveryState: '',
    loadType: '',
    sortBy: 'newest'
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [jobs, filters]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const response = await jobsApi.getAvailable();
      setJobs(response.data || []);
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...jobs];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(job => 
        job.title.toLowerCase().includes(searchLower) ||
        job.pickup_city.toLowerCase().includes(searchLower) ||
        job.delivery_city.toLowerCase().includes(searchLower) ||
        job.description.toLowerCase().includes(searchLower)
      );
    }

    // Pickup state filter
    if (filters.pickupState) {
      result = result.filter(job => job.pickup_state === filters.pickupState);
    }

    // Delivery state filter
    if (filters.deliveryState) {
      result = result.filter(job => job.delivery_state === filters.deliveryState);
    }

    // Load type filter
    if (filters.loadType) {
      result = result.filter(job => job.load_type === filters.loadType);
    }

    // Sorting
    switch (filters.sortBy) {
      case 'oldest':
        result.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        break;
      case 'weight_high':
        result.sort((a, b) => b.weight_kg - a.weight_kg);
        break;
      case 'weight_low':
        result.sort((a, b) => a.weight_kg - b.weight_kg);
        break;
      default: // newest
        result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }

    setFilteredJobs(result);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      pickupState: '',
      deliveryState: '',
      loadType: '',
      sortBy: 'newest'
    });
  };

  const hasActiveFilters = filters.search || filters.pickupState || filters.deliveryState || filters.loadType;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 sticky top-0 z-40 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/provider" className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
                  <Truck className="w-6 h-6 text-white" />
                </div>
                <span className="font-bold text-xl hidden sm:block" style={{ fontFamily: 'Barlow Condensed' }}>
                  THM
                </span>
              </Link>
              <div className="h-6 w-px bg-border hidden sm:block" />
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                BROWSE JOBS
              </h1>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={fetchJobs} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button 
                variant={showFilters ? "default" : "outline"} 
                size="sm" 
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <Badge className="ml-2 bg-primary/20 text-primary text-xs">Active</Badge>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Search & Filters */}
        <div className="mb-6 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search by title, city, or description..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="pl-10 h-12"
              data-testid="job-search-input"
            />
            {filters.search && (
              <button 
                onClick={() => setFilters(prev => ({ ...prev, search: '' }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-card border border-border rounded-sm p-4"
            >
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Pickup State</Label>
                  <Select 
                    value={filters.pickupState} 
                    onValueChange={(v) => setFilters(prev => ({ ...prev, pickupState: v }))}
                  >
                    <SelectTrigger data-testid="filter-pickup-state">
                      <SelectValue placeholder="All States" />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map(state => (
                        <SelectItem key={state.code || 'all'} value={state.code}>{state.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Delivery State</Label>
                  <Select 
                    value={filters.deliveryState} 
                    onValueChange={(v) => setFilters(prev => ({ ...prev, deliveryState: v }))}
                  >
                    <SelectTrigger data-testid="filter-delivery-state">
                      <SelectValue placeholder="All States" />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map(state => (
                        <SelectItem key={state.code || 'all-del'} value={state.code}>{state.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Load Type</Label>
                  <Select 
                    value={filters.loadType} 
                    onValueChange={(v) => setFilters(prev => ({ ...prev, loadType: v }))}
                  >
                    <SelectTrigger data-testid="filter-load-type">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      {loadTypes.map(type => (
                        <SelectItem key={type.value || 'all-type'} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Sort By</Label>
                  <Select 
                    value={filters.sortBy} 
                    onValueChange={(v) => setFilters(prev => ({ ...prev, sortBy: v }))}
                  >
                    <SelectTrigger data-testid="filter-sort">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {sortOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {hasActiveFilters && (
                <div className="mt-4 pt-4 border-t border-border flex justify-end">
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <X className="w-4 h-4 mr-2" />
                    Clear Filters
                  </Button>
                </div>
              )}
            </motion.div>
          )}
        </div>

        {/* Results Count */}
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {loading ? 'Loading...' : `${filteredJobs.length} jobs available`}
          </p>
        </div>

        {/* Jobs Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Jobs Found</h3>
            <p className="text-muted-foreground mb-4">
              {hasActiveFilters 
                ? "Try adjusting your filters to see more jobs"
                : "There are no available jobs at the moment. Check back soon!"
              }
            </p>
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredJobs.map((job, index) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link 
                  to={`/job/${job.id}`}
                  className="block bg-card border border-border rounded-sm p-5 hover:border-primary/50 transition-colors"
                  data-testid={`job-card-${job.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    {/* Job Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <h3 className="font-semibold text-lg truncate">{job.title}</h3>
                        <Badge variant="outline" className="shrink-0">
                          {job.quote_count}/5 quotes
                        </Badge>
                      </div>
                      
                      {/* Route */}
                      <div className="flex items-center gap-2 text-sm mb-3">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 bg-green-500 rounded-full" />
                          <span>{job.pickup_city}, {job.pickup_state}</span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 bg-blue-500 rounded-full" />
                          <span>{job.delivery_city}, {job.delivery_state}</span>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                          <Package className="w-4 h-4" />
                          <span>{job.load_type}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Weight className="w-4 h-4" />
                          <span>{job.weight_kg.toLocaleString()} kg</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-4 h-4" />
                          <span>Pickup: {formatDate(job.pickup_date)}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-4 h-4" />
                          <span>Deliver by: {formatDate(job.delivery_deadline)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Action */}
                    <div className="lg:pl-4 lg:border-l lg:border-border">
                      <Button className="w-full lg:w-auto btn-active">
                        View & Quote
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
